import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import {
  BarChart3, TrendingUp, Loader2, ArrowDown, ArrowUp,
  CheckCircle, XCircle, Eye, FileText, Sparkles, Award
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

interface FunnelBarProps {
  label: string;
  count: number;
  total: number;
  color: string;
}

const FunnelBar = ({ label, count, total, color }: FunnelBarProps) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-zinc-500 w-24 text-right">{label}</span>
      <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
      <span className="text-sm font-medium text-white w-12 text-right">{count}</span>
      <span className="text-xs text-zinc-600 w-10 text-right">{pct}%</span>
    </div>
  );
};

export default function AdminAnalyticsPage() {
  const { token } = useAuth();
  const [overview, setOverview] = useState<unknown>(null);
  const [vendors, setVendors] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('reputation_score');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, vendorsRes] = await Promise.all([
        fetch(getApiUrl('/api/v2/admin/analytics/overview'), { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(getApiUrl(`/api/v2/admin/analytics/vendors?limit=20&sort_by=${sortBy}`), { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (overviewRes.ok) setOverview(await overviewRes.json());
      else throw new Error('Failed to load analytics');
      if (vendorsRes.ok) setVendors(await vendorsRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetch(getApiUrl(`/api/v2/admin/analytics/vendors?limit=20&sort_by=${sortBy}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : []).then(setVendors);
    }
  }, [sortBy]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-rose-400">{error}</p>
        </div>
      </AdminLayout>
    );
  }

  const ov = overview as { funnel?: Record<string, number>; conversion?: Record<string, number>; total_pitches?: number } | null;
  const f = ov?.funnel || {};
  const c = ov?.conversion || {};
  const total = ov?.total_pitches || 0;
  const vendorList = vendors as Array<{ vendor_company_id: string; vendor_name: string; reputation_score: number; acceptance_rate: number; total_delivered: number; total_accepted: number; total_pitches: number }>;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-rose-500/10">
            <BarChart3 className="w-6 h-6 text-rose-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Analytics</h1>
            <p className="text-zinc-500">Platform-wide pipeline and engagement metrics</p>
          </div>
        </div>

        {/* Conversion Metrics Panel */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="conversion-metrics-panel">
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
            <p className="text-xs text-zinc-500 mb-1">Total Pitches</p>
            <p className="text-2xl font-bold text-white">{total}</p>
          </div>
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
            <p className="text-xs text-zinc-500 mb-1">Valuable Rate</p>
            <p className="text-2xl font-bold text-emerald-400">
              {c.valuable_rate != null ? `${(c.valuable_rate * 100).toFixed(1)}%` : '—'}
            </p>
            <p className="text-xs text-zinc-600">of submitted</p>
          </div>
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
            <p className="text-xs text-zinc-500 mb-1">Delivery Rate</p>
            <p className="text-2xl font-bold text-blue-400">
              {c.delivery_rate != null ? `${(c.delivery_rate * 100).toFixed(1)}%` : '—'}
            </p>
            <p className="text-xs text-zinc-600">of valuable</p>
          </div>
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
            <p className="text-xs text-zinc-500 mb-1">Acceptance Rate</p>
            <p className="text-2xl font-bold text-emerald-400">
              {c.acceptance_rate != null ? `${(c.acceptance_rate * 100).toFixed(1)}%` : '—'}
            </p>
            <p className="text-xs text-zinc-600">{c.total_decided} decided</p>
          </div>
        </div>

        {/* Global Funnel Card */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 p-6" data-testid="global-funnel-card">
          <h2 className="text-base font-semibold text-white mb-5">Pipeline Funnel</h2>
          <div className="space-y-3">
            <FunnelBar label="Draft" count={f.draft || 0} total={total} color="bg-zinc-600" />
            <FunnelBar label="Submitted" count={f.submitted || 0} total={total} color="bg-rose-500" />
            <FunnelBar label="Evaluated" count={f.ai_evaluated || 0} total={total} color="bg-blue-500" />
            <FunnelBar label="Valuable" count={f.valuable || 0} total={total} color="bg-emerald-500" />
            <FunnelBar label="Declined" count={f.declined || 0} total={total} color="bg-rose-600" />
            <FunnelBar label="Delivered" count={f.delivered || 0} total={total} color="bg-blue-400" />
            <FunnelBar label="Accepted" count={f.accepted || 0} total={total} color="bg-emerald-400" />
            <FunnelBar label="Rejected" count={f.rejected || 0} total={total} color="bg-rose-400" />
          </div>
        </div>

        {/* Vendor Ranking Table */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 p-6" data-testid="vendor-ranking-table">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-white">Vendor Rankings</h2>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              data-testid="sort-vendors"
            >
              <option value="reputation_score">Reputation</option>
              <option value="acceptance_rate">Accept Rate</option>
              <option value="total_delivered">Delivered</option>
              <option value="total_accepted">Accepted</option>
            </select>
          </div>

          {vendorList.length === 0 ? (
            <p className="text-center text-zinc-500 py-8">No vendor data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/5">
                    <th className="text-left py-2 pr-3 font-medium">#</th>
                    <th className="text-left py-2 px-3 font-medium">Vendor</th>
                    <th className="text-center py-2 px-3 font-medium">Rep. Score</th>
                    <th className="text-center py-2 px-3 font-medium">Accept Rate</th>
                    <th className="text-center py-2 px-3 font-medium">Delivered</th>
                    <th className="text-center py-2 px-3 font-medium">Accepted</th>
                    <th className="text-center py-2 px-3 font-medium">Pitches</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {vendorList.map((v, i) => {
                    const scoreColor = v.reputation_score >= 1.2 ? 'text-emerald-400' : v.reputation_score >= 0.8 ? 'text-blue-400' : 'text-rose-400';
                    return (
                      <tr key={v.vendor_company_id} className="hover:bg-white/5 transition-colors" data-testid={`vendor-row-${v.vendor_company_id}`}>
                        <td className="py-3 pr-3 text-zinc-600">{i + 1}</td>
                        <td className="py-3 px-3">
                          <p className="font-medium text-white">{v.vendor_name}</p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`font-semibold ${scoreColor}`}>{v.reputation_score}</span>
                        </td>
                        <td className="py-3 px-3 text-center text-zinc-300">{v.acceptance_rate}%</td>
                        <td className="py-3 px-3 text-center text-zinc-300">{v.total_delivered}</td>
                        <td className="py-3 px-3 text-center text-zinc-300">{v.total_accepted}</td>
                        <td className="py-3 px-3 text-center text-zinc-400">{v.total_pitches}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
